import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { ArrowTopRightOnSquareIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { listedNames } from "../../../utils/apiError";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canOpenPage } from "../../../utils/permissions";
import { focusRing } from "../../Exams/TestBuilder/ui";

// A question an active BTS booklet prints does not change (409 question_in_booklet {booklets: [{uuid, name}], lock}):
// its edit, status, delete and move wait until the booklet is back in draft, which an admin does on the booklet's
// page. The refusal says so; whoever opens the BTS pages (an admin) is told to do it and gets the booklets as links, in
// a new tab: the page keeps what the user typed or picked, to try again from. The question's detail says it up front:
// its `booklets` lists those booklets ([] for none), and while it does, its actions are all off.

const asBooklets = (list) => (Array.isArray(list) ? list.filter((booklet) => booklet && typeof booklet === "object") : []);

// The booklets a 409 question_in_booklet lists.
export const lockingBooklets = (err) => asBooklets(err?.response?.data?.booklets);

// What a 409 question_in_booklet locks: "question" (a question a booklet prints would change) or "passage" (a question
// would join a passage a booklet prints); null from a backend that does not say.
export const bookletLockKind = (err) => {
  const lock = err?.response?.data?.lock;
  return lock === "question" || lock === "passage" ? lock : null;
};

// The active BTS booklets that print the question, from its detail ([] for none, and from an older backend).
export const questionBooklets = (question) => asBooklets(question?.booklets);

// Whether the user is shown the booklets' links (and asked to return them to draft).
export const opensBooklets = (user) => canOpenPage(user, "/bts");

// Whether the user gets `booklets` as links: whoever opens the BTS pages, for the booklets the server names by uuid.
// A text for them need not name the booklets (the links do).
export const linksBooklets = (user, booklets) => opensBooklets(user) && asBooklets(booklets).some((booklet) => booklet.uuid);

// The refusal: `whatKey` says what the booklets stop (it takes {{names}}), then who returns them to draft. With
// `counted`, `whatKey` has plural forms by the booklets' count (the question page's texts); without it, it is read
// as it is (other keys may have look-alike "_one" siblings, e.g. folders.move.in_booklet_one).
export const bookletLockMessage = (t, whatKey, booklets, linked = false, { counted = false } = {}) => {
  const count = Math.max(booklets.length, 1);
  const names = listedNames(booklets, t);
  return `${t(whatKey, counted ? { names, count } : { names })} ${t(
    linked && booklets.some((booklet) => booklet.uuid) ? "question_detail.booklet_lock.return" : "question_detail.booklet_lock.ask_admin",
    { count }
  )}`;
};

// The booklets as links to their pages, inline after the message (which ends with a colon for those who get them).
export function BookletLinks({ booklets }) {
  const { t } = useTranslation();
  const linked = (booklets || []).filter((booklet) => booklet?.uuid);
  return linked.map((booklet, index) => (
    <React.Fragment key={booklet.uuid}>
      {index > 0 ? ", " : " "}
      <a
        href={`/bts/${booklet.uuid}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`rounded font-semibold underline underline-offset-2 hover:no-underline ${focusRing}`}
      >
        {booklet.name || booklet.uuid}
        <ArrowTopRightOnSquareIcon className="ml-0.5 inline h-3.5 w-3.5 align-[-2px]" aria-hidden="true" />
        <span className="sr-only"> ({t("question_detail.booklet_lock.new_tab")})</span>
      </a>
    </React.Fragment>
  ));
}

/**
 * The calm notice up front on the page of a question the active BTS booklets `booklets` print (the detail's
 * `booklets`): nothing about it changes until they are back in draft. Whoever opens the BTS pages gets them as links
 * (the names are not said twice); anyone else is told that an admin does it. Nothing for no booklets. The ref goes to
 * the notice, which can take the focus (tabIndex -1) when it appears in place of buttons that had it.
 */
export const BookletLockNotice = React.forwardRef(function BookletLockNotice({ booklets, className = "" }, ref) {
  const { t } = useTranslation();
  const { user } = useContext(UserContext) || {};
  const list = asBooklets(booklets);
  if (!list.length) return null;
  const linked = linksBooklets(user, list);
  return (
    <div
      ref={ref}
      role="note"
      tabIndex={-1}
      data-testid="booklet-lock-notice"
      className={`flex items-start gap-3 rounded-2xl bg-blue-50 px-4 py-3 text-sm leading-relaxed text-blue-950 ring-1 ring-inset ring-blue-600/15 focus:outline-none ${className}`}
    >
      <LockClosedIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
      <p className="min-w-0">
        {bookletLockMessage(t, linked ? "question_detail.booklet_lock.notice_linked" : "question_detail.booklet_lock.notice", list, linked, {
          counted: true,
        })}
        {linked && <BookletLinks booklets={list} />}
      </p>
    </div>
  );
});
